import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Formik, useFormikContext, getIn } from 'formik';
import Grid from '@material-ui/core/Grid';
import { Button } from '@material-ui/core';
import { appConfig } from 'brand-config';
import { lookupsJson } from '@ourbranch/lookups';
import { v4 as uuidv4 } from 'uuid';

import Field from 'core/components/form/form.v2';
import { Autocomplete } from 'core/components/autocomplete';
import { TextField } from 'core/components/text-field';
import { Tooltip } from 'core/components/tooltip';
import { useRouteHelper } from 'core/hooks/use-route-helper';
import { useStore } from 'core/store';
import { SubRiskCollapsibleSection } from 'common/components/farm/locations/risks/sub-risks/sub-risk-collapsible-section';
import FormSectionWrapper from 'common/components/form-section-wrapper';
import CollapsibleListItem from 'common/components/farm/locations/risks/sub-risks/collapsible-list-item';
import { buildAssetOptions, buildAssetDetailFields } from '../helpers';
import { AppliedTag } from 'common/components/farm/applied-tag';
import useStyles from '../liability-coverages-styles';
import NotOnPolicyAsset, { assetInitialValuesByType, assetValidationSchemasByType } from './not-on-policy-asset';

const FIELD_NAME = 'farmLiabilityCoverage.additionalAssetsExtensions';
const NOT_ON_POLICY_ID = 'notOnPolicy';

// #34019: Gator/Utility Transport Vehicle rates like an ATV, so it appears alongside ATV in the
// incidental-liability assets section with the same coverage/UM behavior.
const GATOR = 'gatorUtilityTransportVehicle';

const ASSET_TYPE_LABELS = {
  atv: 'ATVs',
  [GATOR]: 'Gator/UTVs',
  golfCart: 'golf carts',
  boat: 'boats',
  unmannedAircraft: 'unmanned aircraft'
};

const COVERAGE_OPTIONS = {
  atv: [{ value: 'recreationalVehicle', label: 'Recreational vehicle liability coverage' }],
  [GATOR]: [{ value: 'recreationalVehicle', label: 'Recreational vehicle liability coverage' }],
  golfCart: [{ value: 'recreationalVehicle', label: 'Recreational vehicle liability coverage' }],
  unmannedAircraft: [{ value: 'unmannedAircraft', label: 'Unmanned aircraft liability coverage' }],
  boat: [{ value: 'watercraft', label: 'Watercraft liability coverage' }]
};

const UM_LIMITS_TEXT = '$25,000 each person / $50,000 each accident';
const TYPES_WITH_UM_LIMITS = ['atv', GATOR, 'golfCart'];

const NOT_ON_POLICY_TYPE_OPTIONS = [
  { id: 'atv', value: 'ATVs and related equipment' },
  { id: GATOR, value: 'Gator/Utility Transport Vehicle' },
  { id: 'golfCart', value: 'Golf carts' },
  { id: 'unmannedAircraft', value: 'Unmanned aircraft systems (Drones)' },
  { id: 'boat', value: 'Watercraft/boats' }
];

const getInitialValuesForType = (type) => ({
  type,
  coverage: COVERAGE_OPTIONS[type]?.[0]?.value || '',
  ...(assetInitialValuesByType[type] || {})
});

const AdditionalAssetsExtensions = ({ disabled = false, onChangeActive }) => {
  const classes = useStyles();
  const { values, setFieldValue, setFieldTouched } = useFormikContext();

  const items = getIn(values, FIELD_NAME) || [];

  const liabilityType = getIn(values, 'farmLiabilityCoverage.type');
  const { inPolicyRoute } = useRouteHelper();
  const {
    account: {
      policies: {
        policy: { policy: policyData }
      }
    }
  } = useStore();
  const state = inPolicyRoute
    ? policyData?.state
    : getIn(values, 'correctedAddress.state') || getIn(values, 'farmLocations.0.address.state');
  // ATV cc boundary is state-specific. MO (CFM, AAIS) uses a 600cc boundary per the
  // "MO/IN Liability - ATV Type Key"; other states fall back to the flat 500cc list.
  // (IN/Oakwood also uses 600cc per the key but is still on the legacy plan — add it to
  // ccByState when Oakwood flips to AAIS.) See gobranch/branch#31792.
  const ccOptions = lookupsJson.farm.ccByState?.[state] || lookupsJson.farm.cc;
  const typeConfig = appConfig.farm?.[state]?.liabilityExtensions?.assets?.[liabilityType] || {};
  const enabledTypes = ['atv', GATOR, 'golfCart', 'boat', 'unmannedAircraft'].filter((t) => typeConfig[t]);
  const enabledTypesLabel = enabledTypes.map((t) => ASSET_TYPE_LABELS[t]).join(', ');
  const typeSelectOptions = NOT_ON_POLICY_TYPE_OPTIONS.filter((opt) => typeConfig[opt.id]);

  const assetOptions = useMemo(
    () => buildAssetOptions(values.farmLocations, typeConfig),
    [values.farmLocations, typeConfig]
  );

  const [inputValue, setInputValue] = useState('');
  const [selectedAsset, setSelectedAsset] = useState(null);

  const handleSelectChange = useCallback((_event, val) => {
    setInputValue(val?.text || '');
    setSelectedAsset(val);
  }, []);

  const handleInputChange = useCallback((event) => {
    setInputValue(event.target.value);
    setSelectedAsset(null);
  }, []);

  const handleBlur = useCallback(() => {
    if (!selectedAsset) {
      setInputValue('');
    }
  }, [selectedAsset]);

  const handleNotOnPolicySubmit = useCallback(
    (formValues) => {
      const newItem = {
        id: uuidv4(),
        type: formValues.type,
        locationId: null,
        riskId: null,
        existingAssetId: null,
        name: formValues.name || null,
        year: formValues.year != null ? Number(formValues.year) : null,
        make: formValues.make || null,
        model: formValues.model || null,
        serialNumber: formValues.serialNumber || null,
        limit: null,
        description: formValues.description || null,
        coverage: formValues.coverage,
        weight: formValues.weight != null ? Number(formValues.weight) : null,
        cc: formValues.cc != null ? Number(formValues.cc) : null,
        horsepower: formValues.horsepower != null ? Number(formValues.horsepower) : null,
        classification: formValues.classification || null,
        mph: formValues.mph != null ? Number(formValues.mph) : null,
        length: formValues.lengthFt != null ? Number(formValues.lengthFt) : null
      };
      setFieldValue(FIELD_NAME, [...items, newItem]);
      setFieldTouched(FIELD_NAME);
      setSelectedAsset(null);
      setInputValue('');
      onChangeActive();
    },
    [items, setFieldValue, setFieldTouched, onChangeActive]
  );

  const addedAssetIds = useMemo(() => new Set(items.map((item) => item.existingAssetId).filter(Boolean)), [items]);

  const filteredOptions = useMemo(
    () =>
      assetOptions.filter((option) => {
        if (option.id !== NOT_ON_POLICY_ID && addedAssetIds.has(option.itemId)) {
          return false;
        }
        if (!inputValue) {
          return true;
        }
        const text = option.text || '';
        return (
          text.toLowerCase().includes(inputValue.toLowerCase()) ||
          option.id?.toString().toLowerCase().includes(inputValue.toLowerCase())
        );
      }),
    [assetOptions, addedAssetIds, inputValue]
  );

  const currentValue = selectedAsset || (inputValue ? { id: inputValue, text: inputValue } : null);
  const inputValueProp = inputValue ? { inputValue } : {};

  return (
    <div className={classes.sectionContainer} id="farmLiabilityCoverage.additionalAssetsExtensions">
      <SubRiskCollapsibleSection
        type="secondary"
        content={<div className={classes.headerText}>Incidental liability ({enabledTypesLabel})</div>}
        contentRight={items.length > 0 ? <AppliedTag /> : null}
        initialOpen={items.length > 0}
      >
        <FormSectionWrapper
          sectionTitle="Scheduled items"
          totalLabel="items"
          totalCount={items.length}
          emptyStateTitle="There are no items added yet"
          emptyStateSubtitle="To add one select from below"
        >
          {items.map((item, index) => {
            const isNotOnPolicy = !item.locationId && !item.riskId && !item.existingAssetId;
            const itemPath = `${FIELD_NAME}[${index}]`;
            const title = (
              <div className={classes.itemTitleWrapper}>
                <div>{item.name || item.description || ''}</div>
                <div className={classes.itemTypeLabel}>
                  {NOT_ON_POLICY_TYPE_OPTIONS.find((o) => o.id === item.type)?.value || item.type}
                </div>
              </div>
            );
            const onDelete = () => {
              const newItems = items.filter((_, i) => i !== index);
              setFieldValue(FIELD_NAME, newItems);
              setFieldTouched(FIELD_NAME);
              onChangeActive();
            };

            const coverageRadio = (
              <>
                <Grid item xs={12}>
                  <div className={classes.coverageLabel}>Coverage</div>
                </Grid>
                <Field
                  xs={12}
                  type="radio"
                  id={`${itemPath}.coverage`}
                  name={`${itemPath}.coverage`}
                  mode="dark"
                  options={COVERAGE_OPTIONS[item.type] || []}
                  disabled={disabled}
                  labelClassName={classes.coverageRadioLabel}
                  optionsSideBySide={false}
                  onChange={onChangeActive}
                />
              </>
            );

            if (isNotOnPolicy) {
              return (
                <CollapsibleListItem
                  key={`additional-asset-${index}`}
                  title={title}
                  rightSideValues={[
                    {
                      label: '',
                      value: (
                        <span className={classes.onlyCoveredHere}>
                          Only covered here
                          <Tooltip
                            text="This item is covered under liability only and does not appear elsewhere on the policy."
                            smallIcon
                          />
                        </span>
                      )
                    }
                  ]}
                  onDelete={onDelete}
                  disabled={disabled}
                >
                  <NotOnPolicyAsset
                    disabled={disabled}
                    type={item.type}
                    pathPrefix={itemPath}
                    onChange={onChangeActive}
                    ccOptions={ccOptions}
                  />
                  {TYPES_WITH_UM_LIMITS.includes(item.type) && (
                    <Field
                      xs={12}
                      type="value"
                      id={`${itemPath}-umLimits-display`}
                      name={`${itemPath}-umLimits-display`}
                      mode="dark"
                      label="Underinsured motorist limits"
                      formatter={() => UM_LIMITS_TEXT}
                    />
                  )}
                  {coverageRadio}
                </CollapsibleListItem>
              );
            }

            // On-policy: asset details remain read-only (data lives on the policy asset); coverage editable
            const assetDetailFields = buildAssetDetailFields(item);

            return (
              <CollapsibleListItem
                key={`additional-asset-${index}`}
                title={title}
                rightSideValues={[]}
                onDelete={onDelete}
                disabled={disabled}
              >
                {assetDetailFields.map((field) => (
                  <Field
                    key={`${itemPath}-asset-${field.name}`}
                    xs={field.size || 4}
                    type="value"
                    id={`${itemPath}-asset-${field.name}`}
                    name={`${itemPath}-asset-${field.name}`}
                    mode="dark"
                    label={field.label}
                    value={field.value || 'N/A'}
                  />
                ))}
                {TYPES_WITH_UM_LIMITS.includes(item.type) && (
                  <Field
                    xs={12}
                    type="value"
                    id={`${itemPath}-umLimits-display`}
                    name={`${itemPath}-umLimits-display`}
                    mode="dark"
                    label="Underinsured motorist limits"
                    value={UM_LIMITS_TEXT}
                  />
                )}
                {coverageRadio}
              </CollapsibleListItem>
            );
          })}

          <div className={classes.addFormContainer}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Autocomplete
                  value={currentValue}
                  {...inputValueProp}
                  id="assetSearch"
                  name="assetSearch"
                  options={filteredOptions}
                  onSelection={handleSelectChange}
                  onBlur={handleBlur}
                  showDeleteIcon
                  showChevronIcon
                  classes={{ option: classes.autocompleteOption }}
                  renderOption={(option) => (
                    <span className={option.id === NOT_ON_POLICY_ID ? classes.notOnPolicyOption : undefined}>
                      {option.text}
                    </span>
                  )}
                  InputComponent={TextField}
                  inputProps={{
                    label: 'Asset search',
                    mode: 'dark',
                    onChange: handleInputChange,
                    disabled,
                    helperText: 'Search for an item on the policy',
                    FormHelperTextProps: { className: classes.autocompleteHelperText }
                  }}
                />
              </Grid>

              {selectedAsset && selectedAsset.id === NOT_ON_POLICY_ID && (
                <Grid item xs={12}>
                  <Formik
                    initialValues={{ type: '' }}
                    validateOnChange={false}
                    validateOnBlur={false}
                    validate={(formValues) => {
                      const schema = assetValidationSchemasByType[formValues.type];
                      if (!schema) {
                        return {};
                      }
                      try {
                        schema.validateSync(formValues, { abortEarly: false, context: formValues });
                        return {};
                      } catch (err) {
                        const errors = {};
                        err.inner.forEach((error) => {
                          if (error.path) {
                            errors[error.path] = error.message;
                          }
                        });
                        return errors;
                      }
                    }}
                    onSubmit={handleNotOnPolicySubmit}
                  >
                    {({ values: formValues, submitForm, resetForm }) => (
                      <Grid container spacing={2}>
                        <Field
                          xs={12}
                          type="select"
                          name="type"
                          mode="dark"
                          label="Type of item"
                          options={typeSelectOptions}
                          onChange={(value) => {
                            resetForm({ values: getInitialValuesForType(value) });
                          }}
                          disabled={disabled}
                          className={classes.notOnPolicyTypeSelect}
                        />
                        <Grid item xs={12} className={classes.notOnPolicyFieldsContainer}>
                          <Grid container spacing={2}>
                            <NotOnPolicyAsset disabled={disabled} ccOptions={ccOptions} />
                          </Grid>
                        </Grid>
                        {formValues.type && (
                          <>
                            {TYPES_WITH_UM_LIMITS.includes(formValues.type) && (
                              <Field
                                xs={12}
                                type="value"
                                id="not-on-policy-umLimits"
                                name="not-on-policy-umLimits"
                                mode="dark"
                                label="Underinsured motorist limits"
                                value={UM_LIMITS_TEXT}
                              />
                            )}
                            <Grid item xs={12}>
                              <div className={classes.coverageLabel}>Coverage</div>
                            </Grid>
                            <Field
                              xs={12}
                              type="radio"
                              id="coverage"
                              name="coverage"
                              mode="dark"
                              options={COVERAGE_OPTIONS[formValues.type] || []}
                              disabled={disabled}
                              labelClassName={classes.coverageRadioLabel}
                              optionsSideBySide={false}
                            />
                            <Grid container alignItems="center" className={classes.addButtonRow}>
                              <Button
                                type="button"
                                mode="big"
                                variant="contained"
                                color="secondary"
                                className={classes.addButton}
                                disabled={disabled}
                                onClick={submitForm}
                              >
                                Add item
                              </Button>
                            </Grid>
                          </>
                        )}
                      </Grid>
                    )}
                  </Formik>
                </Grid>
              )}

              {selectedAsset && selectedAsset.id !== NOT_ON_POLICY_ID && (
                <Grid item xs={12}>
                  <Formik
                    key={selectedAsset.id}
                    initialValues={{ coverage: COVERAGE_OPTIONS[selectedAsset.assetType]?.[0]?.value || '' }}
                    validateOnChange={false}
                    validateOnBlur={false}
                    onSubmit={(formValues) => {
                      const ext = selectedAsset.extendedDetails || {};
                      const newItem = {
                        id: uuidv4(),
                        type: selectedAsset.assetType,
                        locationId: selectedAsset.locationId,
                        riskId: selectedAsset.riskId,
                        existingAssetId: selectedAsset.itemId,
                        name: selectedAsset.name,
                        year: ext.year,
                        make: ext.make,
                        model: ext.model,
                        serialNumber: ext.serialNumber,
                        limit: selectedAsset.limit?.toString(),
                        description: selectedAsset.description,
                        coverage: formValues.coverage,
                        weight: ext.weight,
                        cc: ext.cc,
                        horsepower: ext.horsepower,
                        classification: ext.classification,
                        mph: ext.milesPerHour,
                        length: ext.lengthFt
                      };
                      setFieldValue(FIELD_NAME, [...items, newItem]);
                      setFieldTouched(FIELD_NAME);
                      setSelectedAsset(null);
                      setInputValue('');
                      onChangeActive();
                    }}
                  >
                    {({ submitForm }) => {
                      const detailFields = buildAssetDetailFields(selectedAsset);
                      return (
                        <Grid container spacing={2}>
                          {detailFields.map((field) => (
                            <Field
                              key={field.name}
                              xs={field.size}
                              type="value"
                              id={`asset-preview-${field.name}`}
                              name={`asset-preview-${field.name}`}
                              mode="dark"
                              label={field.label}
                              value={field.value}
                            />
                          ))}
                          {TYPES_WITH_UM_LIMITS.includes(selectedAsset.assetType) && (
                            <Field
                              xs={12}
                              type="value"
                              id="asset-preview-umLimits"
                              name="asset-preview-umLimits"
                              mode="dark"
                              label="Underinsured motorist limits"
                              value={UM_LIMITS_TEXT}
                            />
                          )}
                          <Grid item xs={12}>
                            <div className={classes.coverageLabel}>Coverage</div>
                          </Grid>
                          <Field
                            xs={12}
                            type="radio"
                            id="coverage"
                            name="coverage"
                            mode="dark"
                            options={COVERAGE_OPTIONS[selectedAsset.assetType] || []}
                            disabled={disabled}
                            labelClassName={classes.coverageRadioLabel}
                            optionsSideBySide={false}
                          />
                          <Grid container alignItems="center" className={classes.addButtonRow}>
                            <Button
                              type="button"
                              mode="big"
                              variant="contained"
                              color="secondary"
                              className={classes.addButton}
                              disabled={disabled}
                              onClick={submitForm}
                            >
                              Add item
                            </Button>
                          </Grid>
                        </Grid>
                      );
                    }}
                  </Formik>
                </Grid>
              )}
            </Grid>
          </div>
        </FormSectionWrapper>
      </SubRiskCollapsibleSection>
    </div>
  );
};

AdditionalAssetsExtensions.propTypes = {
  disabled: PropTypes.bool.isRequired,
  onChangeActive: PropTypes.func.isRequired
};

export default AdditionalAssetsExtensions;
